import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Link from "next/link";
import Router from "next/router";
import { Base64 } from "js-base64";
import Cookie from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import { LocalizationProvider, Localized } from "@fluent/react";

import { getQueryParam } from "@kikoff/client-utils/src/dom";
import KButton from "@kikoff/components/src/v1/buttons/KButton";
import LoadableButton from "@kikoff/components/src/v1/buttons/LoadableButton";
import Card from "@kikoff/components/src/v1/cards/Card";
import WithIcon from "@kikoff/components/src/v1/cards/WithIcon";
import Collapsible from "@kikoff/components/src/v1/containers/Collapsible";
import TextInput, {
  TextInputAction,
} from "@kikoff/components/src/v1/inputs/TextInput";
import KLink from "@kikoff/components/src/v1/navigation/KLink";
import { InteractiveError } from "@kikoff/hooks/src/useError";
import useForm from "@kikoff/hooks/src/useForm";
import useObjectState from "@kikoff/hooks/src/useObjectState";
import { web } from "@kikoff/proto/src/protos";
import { webRPC } from "@kikoff/proto/src/rpc";
import { handleFailedStatus, handleProtoStatus } from "@kikoff/utils/src/proto";
import { format } from "@kikoff/utils/src/string";
import yup from "@kikoff/utils/src/yup";

import iconCelebration from "@component/buckets/icon-celebration.svg";
import { ErrorPanel } from "@component/errors";
import { KCheckbox } from "@component/inputs/checkbox";
import Header from "@component/layout/header";
import Line from "@component/line";
import CountryRedirectHeader from "@component/onboarding/country_redirect_header";
import { PasswordRequirements, PasswordStrength } from "@component/password";
import { selectFeatureFlag } from "@feature/page";
import {
  fetchRefereeInfo,
  initUser,
  selectIntroPromo,
  updateUserState,
} from "@feature/user";
import GoogleSignInButton from "@src/components/buttons/GoogleSignInButton";
import { useIntroPromoPoller } from "@src/hooks/promos";
import { useOverlaysController } from "@src/overlay";
import analytics, { track } from "@util/analytics";
import { l10nResources } from "@util/l10n";

import styles from "./index.module.scss";

declare global {
  namespace google.accounts.id {
    interface GsiButtonConfiguration {
      nonce?: string;
    }
  }
}

const csrfToken = uuidv4();

const l10n = l10nResources({
  en: `onboarding-create-ur-acct = Create your account
onboarding-login-here = Log in here
onboarding-already-have-acct = Already have an account?

item-1-short = No credit check
item-1-title = Fast
item-1-long = No credit check, no credit pull.
item-1-icon = 

item-2-short = Plans start at \${$monthlyPayment}/month.
item-2-title = Affordable
item-2-long = Plans start at \${$monthlyPayment}/month.
item-2-icon = 

item-3-short = Bank-level data security
item-3-title = Secure
item-3-long = Bank-level security keeps your data safe.
item-3-icon = 

login-create-acct-failure-default = Unable to create account.
login-create-acct-failure-taken = This email is already in use. Please try another or log in if you already have an account.
email-placeholder = Email
confirm-email-placeholder = Confirm email
password-placeholder = Password
create-acct-button-initial = JOIN KIKOFF
create-acct-button-logged-in = LOGGED IN
create-acct-button-created = ACCOUNT CREATED
create-acct-terms-subtext = By submitting my information, I accept Kikoff’s
create-acct-terms-subtext-explicit = Accept Kikoff's
create-acct-terms-subtext-explicit-error = Please accept the Terms of Service, E-Sign Consent, and Privacy Policy to continue.
terms-of-service = Terms of Service
esign-consent = E-Sign Consent
privacy-policy = Privacy Policy
and = and
password-requirements = Password Requirements
password-must-not-be-weak = Must be at least 8 characters long
password-must-be-long = Must not be very weak
email-required = Email is required
email-invalid = Invalid email
email-must-match = Emails must match
email-confirmation-required = Email confirmation is required
password-too-weak = Password too weak
password-too-short = Password is too short
password-required = Password is required
special-offer-activated = Your special offer is activated!

referee-special-offer = Special offer
referee-gift-sent = sent you a {$referralAmount} gift. Sign up to redeem.
`,
  es: `onboarding-create-ur-acct = Iniciar sesión
onboarding-login-here = Entre aquí
onboarding-already-have-acct = ¿Ya tienes una cuenta?

item-1-short = Sin verificación de crédito
item-1-title = Rápido
item-1-long = Sin verificación de crédito, sin extracción de crédito.
item-1-icon = 

item-2-short = Desde solo \${$monthlyPayment} al mes.
item-2-title = Asequible
item-2-long = Desde solo \${$monthlyPayment} al mes.
item-2-icon = 

item-3-short = Seguridad de datos a nivel bancario
item-3-title = Seguro
item-3-long = La seguridad a nivel bancario mantiene sus datos seguros.
item-3-icon = 

login-create-acct-failure-default = No se puede crear la cuenta.
login-create-acct-failure-taken = Este correo electrónico ya está en uso. Intente con otro o inicie sesión si ya tiene una cuenta.
email-placeholder = Correo electrónico
confirm-email-placeholder = Confirmar correo electrónico
password-placeholder = Clave
create-acct-button-initial = ÚNETE A KIKOFF
create-acct-button-logged-in = CONECTADO
create-acct-button-created = CUENTA CREADA
create-acct-terms-subtext = Al enviar mis datos, acepto los de Kikoff
create-acct-terms-subtext-explicit = Acepta el de Kikoff
create-acct-terms-subtext-explicit-error = Acepte los Términos de servicio, el Consentimiento de firma electrónica y la Política de privacidad para continuar.
terms-of-service = Términos de servicio
esign-consent = Consentimiento de firma electrónica
privacy-policy = Política de privacidad
and = y
password-requirements = Requisitos de Contraseña
password-must-not-be-weak = Debe tener al menos 8 caracteres de longitud
password-must-be-long = No debe ser muy débil
email-required = Correo electronico es requerido
email-invalid = Email inválido
email-must-match = Los correos electrónicos deben coincidir
email-confirmation-required = Se requiere confirmación por correo electrónico
password-too-weak = Contraseña demasiado debil
password-too-short = La contraseña es demasiado corta
password-required = Se requiere contraseña
special-offer-activated = ¡Tu oferta especial está activada!

referee-special-offer = Oferta especial
referee-gift-sent = te envió un regalo de {$referralAmount}. Regístrate para canjear.
`,
});

export const createSubmitHandler = ({
  setButtonStatus,
  dispatch,
  signupToken,
  onSuccess,
  error,
  signupType,
}: {
  setButtonStatus: React.Dispatch<React.SetStateAction<string>>;
  dispatch: ReturnType<typeof useDispatch>;
  signupToken: string;
  onSuccess(data: web.public_.CreateUserResponse);
  error: InteractiveError;
  signupType?: web.public_.OnboardingStatus.SignupType;
}) => (_, data) => {
  window.dispatchEvent(
    new CustomEvent("kikoff:signup", {
      detail: { email: data.email },
    })
  );

  track("Onboarding - Create Account", {
    signupType,
    cookieReferrerToken: Cookie.get("refer"),
    localStorageReferrerToken: localStorage.getItem("refer"),
  });

  setButtonStatus("pending");

  function handleSuccess(res) {
    onSuccess(res);
    setTimeout(() => {
      dispatch(initUser());
    }, 1500);
  }

  webRPC.Onboarding.createUser({
    email: data.email,
    password: data.password,
    referrerToken: Cookie.get("refer") || localStorage.getItem("refer"),
    signupToken,
    signupType,
  })
    .then(
      handleProtoStatus({
        AUTHENTICATED: handleSuccess,
        CREATED(res) {
          analytics.convert("Signup");
          return handleSuccess(res);
        },
        TAKEN(data) {
          track("Onboarding - Create Account - Email Taken");
          return handleFailedStatus(
            l10n.getString("login-create-acct-failure-taken")
          )(data);
        },
        _DEFAULT(data) {
          track("Onboarding - Create Account - Error", {
            title: data?.error?.title,
            description: data?.error?.description,
          });
          return handleFailedStatus(
            l10n.getString("login-create-acct-failure-default")
          )(data);
        },
      })
    )
    .catch((err) => {
      error.throw(err.message);
      setButtonStatus("initial");
    });
};

export const createAccountSchema = (confirmEmail = false) =>
  yup.object({
    email: yup
      .string()
      .email(l10n.getString("email-invalid"))
      .required(l10n.getString("email-required")),
    password: yup
      .string()
      .password(l10n.getString("password-too-weak"))
      .min(8, l10n.getString("password-too-short"))
      .required(l10n.getString("password-required")),
    confirm_email: confirmEmail
      ? yup
          .string()
          .oneOf([yup.ref("email")], l10n.getString("email-must-match"))
          .required(l10n.getString("email-confirmation-required"))
      : yup.string().optional(),
  });

function PromoAppliedCelebration() {
  useIntroPromoPoller();

  const showFreePromo = useSelector(selectIntroPromo());

  useEffect(() => {
    if (showFreePromo) {
      track.impression("Onboarding - Create Account - Intro Promo", {
        promo: showFreePromo.name,
      });
    }
  }, []);

  if (!showFreePromo) {
    return null;
  }

  return (
    <div className={styles["promo-celebration"]}>
      <img src={iconCelebration.src} className={styles["celebration-icon"]} />
      <div className="text:heading-4++ my-2">
        {l10n.getString("special-offer-activated")}
      </div>
    </div>
  );
}

function RefereeSpecialOffer() {
  const refereeInfo = useSelector((state) => state.user.refereeInfo);
  if (!refereeInfo || refereeInfo.referralAmount === 0) {
    return null;
  }

  return (
    <Card outline className="mb-3">
      <WithIcon name="cashback">
        <div className="text:small+ color:primary">
          {l10n.getString("referee-special-offer")}
        </div>
        <div className="text:regular">
          {refereeInfo?.firstName}{" "}
          {l10n.getString("referee-gift-sent", {
            referralAmount: format.money(refereeInfo?.referralAmount),
          })}
        </div>
      </WithIcon>
    </Card>
  );
}

function OnboardingValueProps({ inline = false }) {
  const monthlyPayment = 5;
  const valuePropIds = ["item-1", "item-2", "item-3"];

  return (
    <div className={styles[inline ? "inline-value-props" : "value-props"]}>
      {valuePropIds.map((id) => (
        <div
          className={styles[inline ? "message" : "message-container"]}
          key={id}
        >
          <div className={styles.icon}>{l10n.getString(`${id}-icon`)}</div>
          {inline ? (
            <>
              <div className={styles.title}>
                {l10n.getString(`${id}-title`)}
              </div>
              <div className={styles.body}>
                {l10n.getString(`${id}-short`, { monthlyPayment })}
              </div>
            </>
          ) : (
            <div className={styles.message}>
              <header>{l10n.getString(`${id}-title`)}</header>
              {l10n.getString(`${id}-long`, { monthlyPayment })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

type SubmitButtonStatus = "initial" | "pending" | "success";

export default function CreateAccountV1() {
  const dispatch = useDispatch();

  const preview = useSelector((state) => state.shopping.checkoutPreview);

  const overlays = useOverlaysController();

  const user = useSelector((state) => state.user);
  const ssoEnabled = useSelector(selectFeatureFlag("google_sso"));

  const [initial, setInitial] = useState<{
    email?: string;
    // eslint-disable-next-line camelcase
    signup_token?: string;
    // eslint-disable-next-line camelcase
    no_confirm?: string;
  }>(null);

  const [password, setPassword] = useObjectState({ value: "", focus: false });
  const [buttonStatus, setButtonStatus] = useState<SubmitButtonStatus>(
    "initial"
  );
  const [successText, setSuccessText] = useState("");

  const url = new URL(window.location.href);
  const signupTypeParam = url.searchParams.get("type");
  const isFreemium = signupTypeParam === "freemium";
  const signupType = isFreemium
    ? web.public_.OnboardingStatus.SignupType.FREEMIUM
    : web.public_.OnboardingStatus.SignupType.CREDIT;

  const [score, setScore] = useState(0);
  const hiddenState = useState(true);
  const ref = useRef(null);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [showAgreementError, setShowAgreementError] = useState(false);

  const { register, handleSubmit, submittable, error } = useForm({
    schema: useMemo(() => createAccountSchema(), []),
    defaultRegisterOptions: {
      include: ["error", "valid"],
    },
    fieldDefaults: {
      email: initial?.email,
    },
  });

  useEffect(() => {
    track.impression("Onboarding - Signup Screen", { explicitTos: true });

    if (!preview && Cookie.get("plan")) {
      Router.replace("/onboarding-with-plan/get-started");
      return;
    }

    const signupData = getQueryParam("signup_data");
    if (Cookie.get("refer")) {
      dispatch(fetchRefereeInfo(Cookie.get("refer")));
    }

    if (signupData !== null) {
      const decodedString = Base64.decode(signupData);
      const json = JSON.parse(decodedString);
      setInitial(json);
      track("Onboarding - Signup Screen - Email Pre-Filled", {
        email: json.email,
      });
    } else {
      setInitial({});
    }

    Cookie.set("k_csrf_token", csrfToken, {
      domain: window.location.host,
    });
  }, []);

  useEffect(() => {
    // XXX: removing overlays handles an edgecase when refreshing page with
    //   an overlay open, then closing via "X" to logout. An invisible
    //   overlay can remain over the login form.
    overlays.removeAll();

    if (user.mfaRequired) {
      overlays.push("identity_verification/mfa", {
        purpose: "USER_LOGIN",
      });
    }
  }, [user.mfaRequired]);

  if (initial === null) return null;

  const handleSuccess = (text) => () => {
    setSuccessText(text);
    setButtonStatus("success");
    setTimeout(() => {
      setButtonStatus("pending");

      setTimeout(() => {
        error.throw("Something went wrong, please reload the page");
      }, 5000);
    }, 1500);
  };

  return (
    <div className={styles["create-account-page"]} ref={ref}>
      <LocalizationProvider l10n={l10n}>
        <Header className={styles.header} fixed />
        <PromoAppliedCelebration />
        <RefereeSpecialOffer />
        <CountryRedirectHeader className="mb-4" />
        <div className={styles.wrapper}>
          <div className={styles["create-account-container"]}>
            <header className={isFreemium ? styles["left-shift-title"] : null}>
              <Localized id="onboarding-create-ur-acct" />
            </header>
            {isFreemium && (
              <p>
                Create an account to get access to your free credit report
                evaluation.
              </p>
            )}
            {!isFreemium && <OnboardingValueProps inline />}
            <div className={styles["form-container"]}>
              <form
                onSubmit={handleSubmit((...args) => {
                  track.tap("Onboarding - Sign Up Button", {
                    acceptedTos: agreementChecked,
                  });
                  if (!agreementChecked) {
                    setShowAgreementError(true);
                    return;
                  }
                  createSubmitHandler({
                    setButtonStatus,
                    dispatch,
                    error,
                    signupToken: initial.signup_token,
                    signupType,
                    onSuccess: handleProtoStatus({
                      AUTHENTICATED({ mfaRequired }) {
                        track("Onboarding - Authenticated User", {
                          explicitTos: true,
                        });
                        // XXX users can log in via the signup form, so must
                        //   support mfa here too.
                        dispatch(updateUserState({ mfaRequired }));
                        track("Auth - Logged In", {
                          mfaRequired,
                          form: "create user",
                        });

                        handleSuccess("Logged in");
                      },
                      CREATED() {
                        track("Onboarding - Created User", {
                          explicitTos: true,
                        });
                        handleSuccess("created");
                      },
                      _DEFAULT: handleFailedStatus(),
                    }),
                  })(...args);
                })}
              >
                <TextInput
                  variant="outlined"
                  label={l10n.getString("email-placeholder")}
                  name="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  {...register("email")}
                />
                <TextInput
                  variant="outlined"
                  label={l10n.getString("password-placeholder")}
                  name="password"
                  autoComplete="new-password"
                  type="password"
                  {...TextInputAction.Visibility.propsWithState(hiddenState)}
                  {...register("password", {
                    include: ["valid"],
                    validationOptions: {
                      context: { string: { password: { score } } },
                      abortEarly: false,
                    },
                    onChange(e) {
                      setPassword({ value: e.currentTarget.value });
                    },
                    onFocus() {
                      setPassword({ focus: true });
                    },
                    // No need to collapse requirements on blur, makes clicking
                    // next input jank
                  })}
                />
                <PasswordStrength
                  password={password.value}
                  showLabel={password.focus}
                  onScoreChange={(_score) => {
                    setScore(_score);
                  }}
                />
                <Collapsible collapse={!password.focus}>
                  <PasswordRequirements
                    reqs={[
                      {
                        description: l10n.getString(
                          "password-must-not-be-weak"
                        ),
                        valid: password.value.length >= 8,
                      },
                      {
                        description: l10n.getString("password-must-be-long"),
                        valid: score > 0,
                      },
                    ]}
                  />
                </Collapsible>
                <div className={styles.agreement}>
                  <KCheckbox
                    propsFor={{ checkbox: { className: styles.checkbox } }}
                    checked={agreementChecked}
                    onChange={(e) => {
                      setAgreementChecked(e.currentTarget.checked);
                      setShowAgreementError(false);
                    }}
                    valid={!showAgreementError}
                    error={
                      showAgreementError &&
                      Error(
                        l10n.getString(
                          "create-acct-terms-subtext-explicit-error"
                        )
                      )
                    }
                  >
                    <Localized id="create-acct-terms-subtext-explicit" />{" "}
                    <TermsOfService />, <ESignConsent />
                    , <Localized id="and" /> <PrivacyPolicy />.
                  </KCheckbox>
                </div>
                <ErrorPanel
                  // @ts-expect-error
                  message={error.message}
                  className={styles.error}
                  show={error.show}
                  onClose={error.hide}
                />
                <KButton
                  className={styles.submit}
                  as={LoadableButton}
                  variant="black"
                  size="full-width"
                  text={{
                    success: successText,
                  }}
                  status={buttonStatus}
                  state={submittable ? "active" : "disabled"}
                  type="submit"
                >
                  Sign Up
                </KButton>
                {ssoEnabled && (
                  <>
                    <Line className="mt-2 mb-1" text="or" />
                    <GoogleSignInButton
                      onStateChange={(state) => {
                        if (state === "initial" || state === "pending") {
                          return setButtonStatus(state);
                        }

                        handleSuccess(
                          {
                            authenticated: "Logged in",
                            created: "Created",
                          }[state]
                        );

                        setTimeout(() => {
                          dispatch(initUser());
                        }, 1500);
                      }}
                      csrfToken={csrfToken}
                      error={error}
                      disabled={!agreementChecked}
                      onClickWhenDisabled={setShowAgreementError}
                      requestType={
                        web.public_.CallbackRequest.RequestType.SIGNUP
                      }
                    />
                  </>
                )}
              </form>
              {!isFreemium && (
                <>
                  <div className={styles.divider} />
                  <OnboardingValueProps />
                </>
              )}
            </div>
            {/* This is in .create-account-container for overflow scrolling, would appear as fixed otherwise */}
            <div className={styles["login-link"]}>
              <Localized id="onboarding-already-have-acct" />{" "}
              <Link href="/login">
                <a>
                  <Localized id="onboarding-login-here" />
                </a>
              </Link>
            </div>
          </div>
        </div>
      </LocalizationProvider>
    </div>
  );
}

function TermsOfService() {
  return (
    <KLink href="https://kikoff.com/terms.pdf" type="pdf">
      <Localized id="terms-of-service" />
    </KLink>
  );
}

function ESignConsent() {
  return (
    <KLink href="https://kikoff.com/e-sign.pdf" type="pdf">
      <Localized id="esign-consent" />
    </KLink>
  );
}

function PrivacyPolicy() {
  return (
    <KLink href="https://kikoff.com/privacy-policy.pdf" type="pdf">
      <Localized id="privacy-policy" />
    </KLink>
  );
}
